import React, { useState } from 'react';
import {
  format, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  addDays, addMonths, subMonths, isSameMonth, isSameDay, isToday, parseISO
} from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { PipelineState, Deal } from './types';
import { cn } from '@/lib/utils';

interface DealCalendarViewProps {
  state: PipelineState;
  onDealClick: (deal: Deal) => void;
  onAddDeal?: (defaultDate?: string) => void;
}

export const DealCalendarView: React.FC<DealCalendarViewProps> = ({
  state, onDealClick, onAddDeal,
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calStart = startOfWeek(monthStart, { weekStartsOn: 0 });
  const calEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });

  // Collect all deals from all stages + won + lost
  const allDeals = Object.values(state.dealsById || {});

  const getDealsForDay = (day: Date): Deal[] => {
    return allDeals.filter(deal => {
      const dateStr = deal.expectedCloseDate || deal.createdAt;
      if (!dateStr) return false;
      try {
        const parsed = typeof dateStr === 'string' ? parseISO(dateStr) : new Date(dateStr);
        return isSameDay(parsed, day);
      } catch {
        return false;
      }
    });
  };

  const weeks: Date[][] = [];
  let current = calStart;
  while (current <= calEnd) {
    const week: Date[] = [];
    for (let i = 0; i < 7; i++) {
      week.push(current);
      current = addDays(current, 1);
    }
    weeks.push(week);
  }

  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="flex flex-col h-full bg-card rounded-2xl border border-border overflow-hidden shadow-sm">
      {/* Calendar Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-card">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-bold text-foreground">
            {format(currentDate, 'MMMM yyyy')}
          </h2>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentDate(new Date())}
            className="h-8 text-xs font-medium border-border"
          >
            Today
          </Button>
        </div>

        <div className="flex items-center gap-1.5">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCurrentDate(prev => subMonths(prev, 1))}
            className="h-8 w-8 text-muted-foreground hover:text-foreground"
            title="Previous month"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCurrentDate(prev => addMonths(prev, 1))}
            className="h-8 w-8 text-muted-foreground hover:text-foreground"
            title="Next month"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 border-b border-border bg-muted/20">
        {daysOfWeek.map(day => (
          <div
            key={day}
            className="py-2.5 text-center text-xs font-semibold text-muted-foreground tracking-wider uppercase"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Month grid */}
      <div className="flex-1 grid grid-rows-5 md:grid-rows-6 divide-y divide-border overflow-y-auto">
        {weeks.map((week, weekIdx) => (
          <div key={weekIdx} className="grid grid-cols-7 divide-x divide-border min-h-[110px]">
            {week.map(day => {
              const inCurrentMonth = isSameMonth(day, currentDate);
              const isCurrentDay = isToday(day);
              const dayDeals = getDealsForDay(day);

              return (
                <div
                  key={day.toISOString()}
                  onClick={() => onAddDeal?.(format(day, 'yyyy-MM-dd'))}
                  className={cn(
                    'p-1.5 md:p-2 flex flex-col gap-1 transition-colors group relative cursor-pointer',
                    !inCurrentMonth && 'bg-muted/10 opacity-40',
                    inCurrentMonth && 'hover:bg-muted/30',
                    isCurrentDay && 'bg-workspace-accent/[0.03]'
                  )}
                >
                  {/* Day header */}
                  <div className="flex items-center justify-between">
                    <span
                      className={cn(
                        'text-xs font-medium inline-flex items-center justify-center w-6 h-6 rounded-full',
                        isCurrentDay
                          ? 'bg-workspace-accent text-white font-bold'
                          : inCurrentMonth
                          ? 'text-foreground'
                          : 'text-muted-foreground'
                      )}
                    >
                      {format(day, 'd')}
                    </span>
                    {dayDeals.length > 0 && (
                      <span className="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">
                        ${dayDeals.reduce((sum, d) => sum + (d.value || 0), 0).toLocaleString()}
                      </span>
                    )}
                  </div>

                  {/* Deals for day */}
                  <div className="flex-1 space-y-1 overflow-y-auto max-h-[80px] scrollbar-none">
                    {dayDeals.slice(0, 3).map(deal => {
                      const stage = deal.stage_id ? state.stageMeta[deal.stage_id] : null;
                      return (
                        <div
                          key={deal.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            onDealClick(deal);
                          }}
                          className="px-1.5 py-1 rounded-md bg-card border border-border shadow-xs hover:border-workspace-accent/60 hover:shadow-sm transition-all cursor-pointer group/deal"
                        >
                          <div className="flex items-center justify-between gap-1">
                            <span className="text-[11px] font-medium text-foreground truncate group-hover/deal:text-workspace-accent">
                              {deal.title}
                            </span>
                            {deal.value > 0 && (
                              <span className="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 shrink-0">
                                ${deal.value.toLocaleString()}
                              </span>
                            )}
                          </div>
                          {stage && (
                            <div className="flex items-center gap-1 mt-0.5">
                              <span className="w-1.5 h-1.5 rounded-full bg-workspace-accent" />
                              <span className="text-[9px] text-muted-foreground truncate">{stage.title}</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {dayDeals.length > 3 && (
                      <div className="text-[10px] text-muted-foreground font-medium text-center">
                        +{dayDeals.length - 3} more
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};
